import { useReactiveVar } from '@apollo/client'
import { Add, ChevronLeft, ChevronRight, PhotoAlbumOutlined, WhatsApp } from '@mui/icons-material'
import { Box, Button, LinearProgress, List, Slide, Stack, Typography } from '@mui/material'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import { styled, useTheme } from '@mui/material/styles'
import { DebouncedFuncLeading } from 'lodash'
import ReactInfiniteScroll from 'react-infinite-scroll-component'
import { Link } from 'react-router-dom'
import { Bucket, BucketAlbum as BucketAlbumGraphType } from '../../../../../graphql/generated'
import { drawerVar } from '../../../../../providers/apollo/cache'
import SearchBar from '../../appbar/SearchBar'
import AccountMenu from '../components/AccountMenu'
import BucketAlbum from '../components/Album'
import AlbumSubHeader from '../components/AlbumSubHeader'
import BucketSwitcher from '../components/BucketSwitcher'
import StaticAlbums from '../components/StaticAlbums'

interface Props {
  bucketId: string
  userId: string
  drawerSection: 'albums' | 'main'
  bucket: Bucket | null | undefined
  showWhatsAppButton: boolean | null | undefined
  loading: boolean
  hasActiveWhatsAppIntegration: boolean | undefined
  handleOpenAlbums: () => void
  handleBack: () => void
  createBucket: () => void
  loadingMore: boolean
  freemiumUserReachedMaxBuckets: boolean
  canCreateBucket: boolean
  onClickWhatsApp: () => void
  albums: BucketAlbumGraphType[]
  nextToken: string | null | undefined
  fetchMore: DebouncedFuncLeading<() => void>
}

const StyledSwipeableDrawer = styled(SwipeableDrawer)(() => ({
  width: '280px',
  '& a': {
    color: 'inherit'
  },
  '& #album-menu': {
    width: '280px'
  }
}))

export default function MobileDrawer({
  bucketId,
  userId,
  drawerSection,
  bucket,
  loading,
  createBucket,
  loadingMore,
  handleBack,
  freemiumUserReachedMaxBuckets,
  hasActiveWhatsAppIntegration,
  showWhatsAppButton,
  canCreateBucket,
  handleOpenAlbums,
  onClickWhatsApp,
  albums,
  nextToken,
  fetchMore
}: Readonly<Props>) {
  const theme = useTheme()
  const drawer = useReactiveVar(drawerVar)

  const handleOpen = () => drawerVar('main')
  const handleClose = () => drawerVar(null)

  const AlbumSection = (
    <Box
      sx={{
        flex: 1,
        position: 'relative',
        overflowX: 'hidden',
        padding: 0,
        minWidth: '264px'
      }}
    >
      {loading && <LinearProgress color="primary" />}
      <List sx={{ flex: 'auto', padding: '0 8px' }}>
        <ReactInfiniteScroll
          dataLength={albums.length || 0}
          loader={
            <Typography variant="h6" align="center">
              Loading...
            </Typography>
          }
          next={fetchMore}
          hasMore={Boolean(nextToken)}
        >
          {!loading && <AlbumSubHeader bucketId={bucketId} />}
          {albums.map((album) => (
            <BucketAlbum album={album} key={album.id} bucketId={bucketId} />
          ))}
          {Boolean(nextToken) && (
            <Button disabled={loadingMore} onClick={fetchMore} fullWidth>
              load more
            </Button>
          )}
          <Box sx={{ padding: '0 8px' }}>
            <StaticAlbums />
          </Box>
        </ReactInfiniteScroll>
      </List>
    </Box>
  )

  return (
    <StyledSwipeableDrawer
      anchor={theme.direction === 'rtl' ? 'right' : 'left'}
      onClose={handleClose}
      onOpen={handleOpen}
      open={drawer === 'main'}
      variant="temporary"
      swipeAreaWidth={0}
      keepMounted
      PaperProps={{ id: 'album-menu' }}
      sx={{
        height: '100%',
        [`& .MuiDrawer-paper`]: {
          position: 'static',
          width: '280px',
          overflowX: 'hidden',
          height: '100%'
        }
      }}
    >
      {bucketId && (
        <Box sx={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Box sx={{ padding: '8px 16px 0', maxWidth: '280px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mb: '8px' }}>
              {drawerSection === 'main' ? (
                <>
                  <Typography component="h3" sx={{ fontSize: '1.5rem', fontWeight: '600', lineHeight: 1.25 }}>
                    Buckets
                  </Typography>
                  <Box>
                    <Button
                      disabled={!canCreateBucket || freemiumUserReachedMaxBuckets}
                      variant="contained"
                      color="primary"
                      onClick={createBucket}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '4px',
                        borderRadius: '20px',
                        boxShadow: 'none',
                        minWidth: 'auto',
                        padding: '4px 12px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        height: '24px',
                        '& .MuiButton-startIcon': {
                          marginRight: 0
                        }
                      }}
                      startIcon={<Add sx={{ fontSize: '16px' }} />}
                    >
                      Bucket
                    </Button>
                  </Box>
                </>
              ) : (
                <Button
                  onClick={handleBack}
                  startIcon={<ChevronLeft />}
                  sx={{
                    padding: 0,
                    minWidth: 0,
                    color: theme.palette.text.primary,
                    ':disabled': { color: theme.palette.text.primary },
                    ':hover': { backgroundColor: 'transparent', color: theme.palette.primary.main }
                  }}
                >
                  <Typography component="h3" sx={{ fontSize: '0.875rem', fontWeight: '600', lineHeight: 1.25 }}>
                    Back
                  </Typography>
                </Button>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              position: 'relative',
              height: '100%',
              flexDirection: 'column',
              display: 'flex'
            }}
          >
            <Stack
              direction={drawerSection === 'main' ? 'column' : 'row'}
              sx={{ gap: '8px', minHeight: drawerSection === 'main' ? 0 : '100%', height: '100%' }}
            >
              <Slide direction="right" in={drawerSection === 'main'} mountOnEnter unmountOnExit>
                <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', padding: '8px 8px 0px' }}>
                  <Box sx={{ flex: '1 0 0', maxHeight: 'fit-content', minHeight: 0, overflowY: 'auto' }}>
                    <Box>
                      <BucketSwitcher userId={userId} drawerSection={drawerSection} />
                    </Box>

                    <Box sx={{ paddingRight: '8px' }}>
                      <Link
                        to="/buckets"
                        style={{
                          textDecoration: 'none',
                          color: theme.palette.secondary.main,
                          fontSize: '.875rem',
                          fontWeight: 500,
                          display: 'block',
                          textAlign: 'right',
                          padding: '4px 0'
                        }}
                      >
                        View All
                      </Link>
                    </Box>
                  </Box>

                  {!bucket?.isBanned && (
                    <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
                      <Button
                        onClick={handleOpenAlbums}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                          color: theme.palette.text.primary,
                          padding: '8px 0',
                          fontSize: '1.125rem'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <PhotoAlbumOutlined />
                          {albums.length} Albums
                        </Box>
                        <ChevronRight />
                      </Button>
                    </Box>
                  )}
                </Box>
              </Slide>
              {!bucket?.isBanned && drawerSection === 'albums' && (
                <Slide direction="left" in={drawerSection === 'albums'} mountOnEnter unmountOnExit>
                  {AlbumSection}
                </Slide>
              )}
            </Stack>
            {!bucket?.isBanned && (
              <>
                {drawerSection === 'main' && (
                  <Box
                    sx={{
                      padding: '8px 0',
                      display: { xs: 'block', lg: 'none' }
                    }}
                  >
                    <SearchBar />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      )}
      {drawerSection === 'main' && (
        <Box
          sx={{
            marginTop: 'auto',
            borderTop: `1px solid ${theme.palette.divider}`,
            minHeight: showWhatsAppButton ? '95px' : '50px'
          }}
        >
          {showWhatsAppButton && (
            <Box>
              <Button
                fullWidth
                variant="text"
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  alignContent: 'center',
                  color: '#101828',
                  gap: '4px',
                  padding: '8px',
                  borderBottom: `1px solid ${theme.palette.customGrey[200]}`,
                  '.MuiListItem-root': {
                    padding: 0
                  },
                  '&:hover': {
                    background: 'transparent',
                    color: theme.palette.primary.main
                  }
                }}
                onClick={onClickWhatsApp}
              >
                <WhatsApp />
                <Typography sx={{ fontWeight: '500', fontSize: '16px', alignContent: 'center' }}>
                  {hasActiveWhatsAppIntegration ? 'Manage WhatsApp' : 'Connect to WhatsApp'}
                </Typography>
                {!hasActiveWhatsAppIntegration && (
                  <Typography
                    component="span"
                    sx={{ backgroundColor: '#35E8B1', borderRadius: '1px', padding: '1px 4px', fontWeight: '700', fontSize: '10px', ml: '4px' }}
                  >
                    new
                  </Typography>
                )}
              </Button>
            </Box>
          )}
          <AccountMenu />
        </Box>
      )}
    </StyledSwipeableDrawer>
  )
}
